import { useState } from 'react';
import styled from 'styled-components';
import RecordingImage from 'components/RecordingImage';
import { Link } from 'react-router-dom';
import { RecordingEntity } from 'libs/services/s4pTypes';
import {
  Type,
  gray10,
  screenXsMin,
  IconHelpAlt,
  spacer4,
  spacer12,
  spacer24,
  ButtonIcon,
  Popover,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';

const InfoPrompt = styled.div`
  opacity: 0;
  position: absolute;
  bottom: 0;

  tr:hover &,
  tr:focus-within &,
  &:focus-within {
    opacity: 1;
  }
`;

const Cell = styled<any>('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
  opacity: ${(props) => props.greyedOut && '0.4'};

  > :first-child {
    margin-right: ${spacer12};

    @media (min-width: ${screenXsMin}) {
      margin-right: ${spacer24};
    }
  }
`;

const RecordingTitle = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
`;

export const TitleLink = styled(Link)`
  color: ${gray10};
  text-decoration: none;
  font-weight: bold;
  width: fit-content;

  &:hover {
    color: black;
    text-decoration: underline;
  }
`;

const RecordingMetadata = styled.div`
  align-self: stretch;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CurationHelpIcon = styled(ButtonIcon)`
  margin-left: ${spacer4};
`;

const HelpPopover = styled(Popover)`
  position: absolute;
  z-index: 1;
  top: 25px;
  right: 0px;
`;

type Props = {
  recording: RecordingEntity;
  link?: string;
  gTagManager: any;
  showAlbum?: boolean;
  imageSize?: 'S' | 'L';
  greyedOut?: boolean;
  infoPrompt?: React.ReactNode;
  showCurationHelp?: boolean;
  helpPopover?: React.ReactNode;
  helpPopOverTitle?: string;
};

export default function RecordingDetailsCell(props: Props) {
  const t = useT();
  const {
    recording,
    showAlbum,
    imageSize,
    link,
    greyedOut,
    gTagManager,
    infoPrompt,
    helpPopover,
    helpPopOverTitle,
  } = props;
  const [showPopover, setShowPopover] = useState(false);
  return (
    <Cell greyedOut={greyedOut}>
      <RecordingImage
        recording={recording}
        size={imageSize || 'S'}
        trackingEvent="playRecordingFromList"
      />
      <RecordingMetadata>
        <RecordingTitle>
          {link ? (
            <TitleLink
              to={link}
              onClick={() =>
                gTagManager({
                  event: 'selectRecording',
                  recordingGid: recording.gid,
                  recordingTitle: recording.title,
                  recordingArtist: recording.artistName,
                })
              }
            >
              {recording.title}
            </TitleLink>
          ) : (
            <Type semanticColor="textSubdued">{recording.title}</Type>
          )}
          {helpPopover && (
            <CurationHelpIcon onClick={() => setShowPopover(!showPopover)}>
              <IconHelpAlt iconSize={16} />
            </CurationHelpIcon>
          )}
          {showPopover && (
            <HelpPopover
              arrow={Popover.topRight}
              popoverTitle={helpPopOverTitle}
              onClose={() => setShowPopover(false)}
            >
              {helpPopover}
            </HelpPopover>
          )}
        </RecordingTitle>
        <div>
          {showAlbum ? (
            t(
              'TEXT_RECORDINGDETAILS_0',
              'by {artist} • Album: {album}',
              'Details about a recording: the artist name and the album name',
              { artist: recording.artistName, album: recording.albumName },
            )
          ) : (
            <Type variant="viola">{recording.artistName}</Type>
          )}
        </div>
        {infoPrompt ? <InfoPrompt>{infoPrompt}</InfoPrompt> : null}
      </RecordingMetadata>
    </Cell>
  );
}
